// import { ARTICLES_PER_PAGE } from '@/data/constants'
import type { Story } from '@/types'

type QueryParams = {
  starts_with?: string
  per_page?: number
  page?: number
  sort_by?: string
  resolve_relations?: string[]
  version?: 'draft' | 'published'
  [k: string]: any
}

// extend Headers interface globally

declare global {
  interface Headers {
    total?: string
  }
}

export const useLoadMore = (queryParams: QueryParams) => {
  const storyblokApi = useStoryblokApi()
  const config = useRuntimeConfig()
  const articlesPerPage = ref(8)
  const totalElements = ref<number>(0)
  const loadedElements = ref<Story<any>[]>([])
  const nextPageToLoad = ref(1)
  const isError = ref(false)
  const isLoading = ref(false)

  const hideLoadButton = computed(() => {
    if (articlesPerPage.value >= totalElements.value) {
      return true
    }

    if (loadedElements.value.length >= totalElements.value) {
      return true
    }

    return false
  })

  const loadFirstPage = async () => {
    const params = {
      per_page: queryParams.per_page || articlesPerPage.value,
      page: nextPageToLoad.value,
      sort_by: queryParams.sort_by || 'first_published_at:desc',
      version: config.public.storyblokApiVersion as 'draft' | 'published',
      ...queryParams,
    }

    articlesPerPage.value = queryParams.per_page || articlesPerPage.value

    isLoading.value = true

    const { data } = await useAsyncData(
      'first-loaded-elements',
      async () => await storyblokApi.get('cdn/stories', params),
    )

    isLoading.value = false

    if (!data.value?.data.stories) return

    totalElements.value = data.value.headers?.total
      ? +data.value.headers?.total
      : 0

    loadedElements.value = [
      ...loadedElements.value,
      ...data.value?.data.stories,
    ]

    nextPageToLoad.value++
  }

  const loadMore = async () => {
    const params = {
      per_page: queryParams.per_page || articlesPerPage.value,
      page: nextPageToLoad.value,
      sort_by: queryParams.sort_by || 'first_published_at:desc',
      version: config.public.storyblokApiVersion as 'draft' | 'published',
      ...queryParams,
    }

    articlesPerPage.value = queryParams.per_page || articlesPerPage.value

    isLoading.value = true

    let data, headers

    await storyblokApi
      .get(`cdn/stories`, params)
      .then((res) => {
        data = res.data
        headers = res.headers
      })
      .catch((err) => {
        isError.value = true
        console.error(err)
        return err
      })

    isLoading.value = false

    if (!data || !headers) {
      isError.value = true
      return
    }

    totalElements.value = headers?.total ? +headers?.total : 0

    loadedElements.value = [...loadedElements.value, ...data.stories]

    nextPageToLoad.value++
  }

  const initObserver = (targetEl: HTMLElement | null) => {
    if (!targetEl) return

    const observer = new IntersectionObserver(
      (ent) => {
        if (ent[0].isIntersecting && !isLoading.value) {
          loadMore()
        }
      },
      {
        threshold: 0.5,
      },
    )

    observer.observe(targetEl)

    return observer
  }

  return {
    loadFirstPage,
    totalElements,
    nextPageToLoad,
    loadedElements,
    isLoading,
    hideLoadButton,
    loadMore,
    initObserver,
    isError,
  }
}
